import { useContext } from 'react';

import { SavedIcon } from '../../../StateManagment/SavedIcon';
import { db } from '../../../firebase/config.js';
import { useFirestore } from '../../../firebase/useFirestore';
import ToggleButton from '../../common/ToggleButton';
import { AGENDA_VIEW_OPTIONS } from '../../../helpers/calendar/resolveAgendaView';
import resolveAgendaOptions, { SNAP_MINUTES, HOUSEHOLD_PLANNING_BUTTON, SCHEDULE_DISPLAY_OPTIONS } from '../../../helpers/calendar/resolveAgendaOptions';
import useSettings from '../../../hooks/Settings.jsx';
import { householdPlanningEnabled } from '../../../helpers/board/resolveBoardView';

/**
 * Agenda-instellingen per organisatie: weekend verbergen, kwartier- of
 * halfuurraster, en welke weergaven in de knoppenbalk staan.
 *
 * Verzoeken BuurtKlusBedrijf (2026-09-28): 'zaterdag en zondag verbergen',
 * 'op kwartieren kunnen slepen', 'kiezen welke agendaweergaven zichtbaar zijn',
 * en 'aanwezigheid als achtergrond' — die laatste alleen met FIXtool: bij een
 * dagbesteding ís het rooster de aanwezigheidsregistratie.
 *
 * De schakelaar per weergave heet `AgendaViews.Hidden.<weergave>` en is dus
 * omgekeerd aan wat de gebruiker leest ('zichtbaar'): de schakelaar ís het
 * veld, en 'verbergen' is de uitzondering. Zelfde patroon als de kolommen van
 * het huishoudenoverzicht. Alles verbergen kan niet: dan negeert de agenda de
 * instelling (resolveAgendaOptions), dus de laatste weergave uitzetten heeft
 * geen effect — dat staat in de tekst.
 * AI-CONTEXT: See docs/features/agenda-instellingen.md
 *
 * @returns {JSX.Element}
 */
const AgendaSettings = () => {
    const [, setSaved] = useContext(SavedIcon);
    const compagny = useFirestore('CompagnyMeta');
    // Strikt: zolang de instellingen laden staat de keuze er niet.
    const fixtool = householdPlanningEnabled(useSettings());

    /**
     * @param {string} docid
     * @param {string} waarde - '15' of '30' uit de keuzelijst
     */
    const snapHandler = (docid, waarde) => {
        const minuten = Number(waarde);
        if (!SNAP_MINUTES.includes(minuten)) return;
        db.collection('CompagnyMeta').doc(docid).update({ AgendaSnapMinutes: minuten })
            .then(() => setSaved('flex'));
    };

    /**
     * @param {string} docid
     * @param {string} waarde - Een waarde uit SCHEDULE_DISPLAY_OPTIONS
     */
    const scheduleDisplayHandler = (docid, waarde) => {
        if (!SCHEDULE_DISPLAY_OPTIONS.some((option) => option.value === waarde)) return;
        db.collection('CompagnyMeta').doc(docid).update({ AgendaScheduleDisplay: waarde })
            .then(() => setSaved('flex'));
    };

    return (
        <div className="dashboard-container">
            <h2>Agenda</h2>
            <p>
                Stel in hoe de agenda eruitziet voor iedereen in de organisatie. Wisselen van weergave
                in de agenda zelf blijft mogelijk voor de weergaven die aanstaan.
            </p>
            {compagny && compagny.map((comp) => (
                <div key={comp.ID}>
                    <div className="settings-general-module-settings-item-container">
                        <p>Zaterdag en zondag verbergen</p>
                        <ToggleButton
                            docid={comp.docid}
                            id="AgendaHideWeekends"
                            collection="CompagnyMeta"
                            value={comp.AgendaHideWeekends}
                        />
                    </div>
                    <div className="settings-general-module-settings-item-container">
                        <p>Tijdvakken bij het slepen</p>
                        <select
                            aria-label="Tijdvakken bij het slepen"
                            value={Number(comp.AgendaSnapMinutes) === 15 ? '15' : '30'}
                            onChange={(e) => snapHandler(comp.docid, e.target.value)}
                        >
                            <option value="30">Half uur</option>
                            <option value="15">Kwartier</option>
                        </select>
                    </div>
                    {fixtool && (
                        <div className="settings-general-module-settings-item-container">
                            <p>Rooster tonen als</p>
                            {/* Gestuurd en via resolveAgendaOptions, zodat een onbekende
                                waarde hier hetzelfde leest als in de agenda: 'Blok'. */}
                            <select
                                aria-label="Rooster tonen als"
                                value={resolveAgendaOptions(comp).scheduleDisplay}
                                onChange={(e) => scheduleDisplayHandler(comp.docid, e.target.value)}
                            >
                                {SCHEDULE_DISPLAY_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <p className="settings-general-module-settings-subheading">Zichtbare weergaven</p>
                    {AGENDA_VIEW_OPTIONS.map((option) => (
                        <div key={option.value} className="settings-general-module-settings-item-container">
                            <p>{option.label} verbergen</p>
                            <ToggleButton
                                docid={comp.docid}
                                id={`AgendaViews.Hidden.${option.value}`}
                                collection="CompagnyMeta"
                                value={comp.AgendaViews?.Hidden?.[option.value]}
                            />
                        </div>
                    ))}
                    {/* Geen weergave maar een knop in dezelfde balk: de ingang
                        naar 'Huishoudens inplannen'. Telt niet mee voor
                        'alles verbergen'. */}
                    <div className="settings-general-module-settings-item-container">
                        <p>Knop 'huishoudens' verbergen</p>
                        <ToggleButton
                            docid={comp.docid}
                            id={`AgendaViews.Hidden.${HOUSEHOLD_PLANNING_BUTTON}`}
                            collection="CompagnyMeta"
                            value={comp.AgendaViews?.Hidden?.[HOUSEHOLD_PLANNING_BUTTON]}
                        />
                    </div>
                    <p className="household-subtasks__hint">
                        Minstens één weergave blijft altijd aan: zet je ze alle vier uit, dan toont de agenda ze alle vier.
                        De knop 'huishoudens' telt daarin niet mee.
                    </p>
                </div>
            ))}
        </div>
    );
};

export default AgendaSettings;
